import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useControllableState } from "../utils/useControllableState";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = Exclude<ThemePreference, "system">;

export interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

export interface ThemeProviderProps extends PropsWithChildren {
  theme?: ThemePreference;
  defaultTheme?: ThemePreference;
  onThemeChange?: (theme: ThemePreference) => void;
  storageKey?: string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredTheme(
  storageKey: string,
  fallback: ThemePreference,
): ThemePreference {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === "light" || stored === "dark" || stored === "system"
      ? stored
      : fallback;
  } catch {
    return fallback;
  }
}

export function ThemeProvider({
  children,
  theme,
  defaultTheme = "system",
  onThemeChange,
  storageKey = "note-ui-theme",
}: ThemeProviderProps) {
  const [initialTheme] = useState(() =>
    getStoredTheme(storageKey, defaultTheme),
  );
  const [preference, setPreference] = useControllableState({
    value: theme,
    defaultValue: initialTheme,
    onChange: onThemeChange,
  });
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const resolvedTheme =
    preference === "system" ? systemTheme : preference;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const updateSystemTheme = () => {
      setSystemTheme(media.matches ? "dark" : "light");
    };
    updateSystemTheme();
    media.addEventListener?.("change", updateSystemTheme);
    return () => media.removeEventListener?.("change", updateSystemTheme);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setTheme = useCallback(
    (nextTheme: ThemePreference) => {
      try {
        window.localStorage.setItem(storageKey, nextTheme);
      } catch {
        // Theme remains usable when storage is unavailable.
      }
      setPreference(nextTheme);
    },
    [setPreference, storageKey],
  );

  const value = useMemo(
    () => ({ preference, resolvedTheme, setTheme }),
    [preference, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return value;
}
